// Task 7 — /admin/teams builder page. Lists the tenant's team hierarchy as
// an indented tree (depth computed client-side from parentTeamId chains),
// with Edit/Archive/Members row actions. Archived teams render dimmed at
// the bottom, display-only (no actions). Structural sibling of
// queue-instructions/pages/QueueInstructionsListPage.tsx.
//
// Owns the tenant users fetch (GET /admin/teams/roster, team.manage-gated)
// — fix G hoisted this up from TeamDialog + MembersPanel, which each
// fetched it independently. Fetched once on mount and again after any
// membership mutation (assign/remove changes a user's teamId, which both
// children read from this list). T2 — switched from the open GET /users
// to the gated roster route so teamId doesn't leak to every session.
import { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { AdminListHeader } from "@/components/admin/AdminListHeader";
import { toast } from "@/components/Toaster";
import { ApiError } from "@/lib/api-client";
import { getApiErrorCode, showApiError } from "@/lib/error-toast";
import { useAuth } from "@/hooks/useAuth";
import { useTeams } from "../hooks/useTeams";
import { teamsApi, type TeamRecord, type TenantUser } from "../api/client";
import { buildChildrenIndex, leadsTeamOrAncestor } from "../lib/tree";
import { TeamDialog } from "../components/TeamDialog";
import { MembersPanel } from "../components/MembersPanel";
import { DelegationDialog } from "../components/DelegationDialog";
import styles from "../teams.module.css";

type DialogState = { mode: "create" } | { mode: "edit"; team: TeamRecord } | null;

// Bound on tree-walk depth — guards pathological (non-cyclic) deep chains.
const DEPTH_CAP = 20;

interface TreeRow {
  team: TeamRecord;
  depth: number;
}

const ROOT = "__root__";

/**
 * Orders active teams as a parent-before-children tree (siblings sorted by
 * name), then appends archived teams flat at the bottom (depth 0 — they're
 * display-only, so hierarchy position no longer matters for them). A team
 * whose parent is missing or itself archived renders as a root. Each team
 * lands in exactly one children-list, so the walk can't revisit a node;
 * teams inside a parentTeamId cycle are unreachable from ROOT and simply
 * don't render. DEPTH_CAP bounds pathological (non-cyclic) deep chains.
 */
function buildRows(teams: TeamRecord[]): TreeRow[] {
  const byId = new Map(teams.map((t) => [t.id, t]));
  const active = teams.filter((t) => !t.archivedAt);
  const archived = teams.filter((t) => t.archivedAt);

  // Archived-parent-promotes-to-root: normalize each active team's
  // effective parent to the ROOT sentinel before handing the list to the
  // shared adjacency builder, so a team whose parent is missing or itself
  // archived renders as a root.
  const withEffectiveParent = active.map((t) => {
    const parent = t.parentTeamId;
    const parentIsActiveTeam = parent !== null && byId.get(parent) && !byId.get(parent)!.archivedAt;
    return parentIsActiveTeam ? t : { ...t, parentTeamId: ROOT };
  });
  const childrenOf = buildChildrenIndex(withEffectiveParent);
  for (const list of childrenOf.values()) {
    list.sort((a, b) => a.name.localeCompare(b.name));
  }

  const rows: TreeRow[] = [];
  function walk(key: string, depth: number) {
    if (depth > DEPTH_CAP) return;
    for (const child of childrenOf.get(key) ?? []) {
      rows.push({ team: child, depth });
      walk(child.id, depth + 1);
    }
  }
  walk(ROOT, 0);

  const archivedSorted = [...archived].sort((a, b) => a.name.localeCompare(b.name));
  for (const t of archivedSorted) rows.push({ team: t, depth: 0 });

  return rows;
}

export function TeamsAdminPage() {
  const { teams, loading, refetch } = useTeams();
  const { user } = useAuth();
  const [dialogState, setDialogState] = useState<DialogState>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [users, setUsers] = useState<TenantUser[]>([]);
  const [delegationTeam, setDelegationTeam] = useState<TeamRecord | null>(null);

  const rows = useMemo(() => buildRows(teams), [teams]);

  // Task 4 (team-hierarchy-t4) — affordance only; the server's
  // isAncestorOrLead is authoritative on the actual delegation routes (see
  // leadsTeamOrAncestor's doc comment for what this approximation misses).
  // Memoized as a Set (/simplify) so the per-row check is O(1) instead of
  // re-walking the ancestor chain — and rebuilding its byId map — per row.
  const delegatableTeamIds = useMemo(() => {
    if (!user) return new Set<string>();
    const isAdmin = user.permissions.includes("admin");
    return new Set(
      teams
        .filter((t) => isAdmin || leadsTeamOrAncestor(user.userId, t.id, teams))
        .map((t) => t.id),
    );
  }, [teams, user]);
  const canDelegate = (team: TeamRecord) => delegatableTeamIds.has(team.id);

  const loadUsers = useCallback(async () => {
    try {
      const res = await teamsApi.roster();
      setUsers(res.users);
    } catch (e) {
      showApiError(e);
    }
  }, []);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  // Membership changes (assign/remove in MembersPanel) flip a user's
  // teamId, which both this page's memberCount column and the users list
  // itself depend on — refresh both together.
  const handleMembersChanged = useCallback(async () => {
    await Promise.all([refetch(), loadUsers()]);
  }, [refetch, loadUsers]);

  async function handleArchive(team: TeamRecord) {
    if (!window.confirm(`Archive team "${team.name}"?`)) return;
    try {
      await teamsApi.archive(team.id);
      if (expandedId === team.id) setExpandedId(null);
      await refetch();
    } catch (e) {
      if (e instanceof ApiError && e.status === 409 && getApiErrorCode(e) === "team_occupied") {
        toast.error("Team still has members or sub-teams");
        return;
      }
      showApiError(e);
    }
  }

  return (
    <div className={styles.listPage}>
      <AdminListHeader
        title="Teams"
        count={teams.length}
        primaryAction={{ label: "New team", onClick: () => setDialogState({ mode: "create" }) }}
      />

      {loading ? (
        <p className={styles.emptyState}>Loading…</p>
      ) : teams.length === 0 ? (
        <div className={styles.emptyState}>
          No teams yet. Teams scope stats and comms to a supervisor's crew.{" "}
          <button
            type="button"
            className={styles.primary}
            style={{ marginLeft: 12 }}
            onClick={() => setDialogState({ mode: "create" })}
          >
            New team
          </button>
        </div>
      ) : (
        <table className={styles.teamsTable}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Lead</th>
              <th>Members</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ team, depth }) => {
              const archived = team.archivedAt !== null;
              // Archived wins over Delegated (they can't co-occur anyway —
              // archive requires an empty team — but keep the order explicit).
              const statusBadge = archived
                ? "Archived"
                : team.activeDelegationCount > 0
                  ? "Delegated"
                  : null;
              // §5.3a edit-as-primary exception — a team row's natural primary
              // action is editing, and TeamDialog displays all current values
              // (name, lead, parent), so the exception's condition is met.
              // Archived rows are display-only and stay non-activatable.
              const openEdit = () => setDialogState({ mode: "edit", team });
              return (
                <Fragment key={team.id}>
                  <tr
                    style={archived ? { opacity: 0.55 } : undefined}
                    className={!archived ? styles.activeRow : undefined}
                    {...(!archived
                      ? {
                          role: "button" as const,
                          tabIndex: 0,
                          onClick: openEdit,
                          onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === "Enter" || e.key === " ") {
                              if (e.key === " ") e.preventDefault();
                              openEdit();
                            }
                          },
                        }
                      : {})}
                  >
                    <td>
                      <span style={{ paddingLeft: depth * 20 }}>{team.name}</span>
                    </td>
                    <td>
                      {team.supervisorName ?? "—"}
                      {!archived && team.supervisorIsActive === false && (
                        <span className={styles.leadInactiveBadge}>Lead deactivated</span>
                      )}
                    </td>
                    <td>{team.memberCount}</td>
                    <td>
                      {statusBadge && <span className={styles.archivedBadge}>{statusBadge}</span>}
                    </td>
                    <td>
                      {!archived && (
                        <div className={styles.rowActions}>
                          {canDelegate(team) && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDelegationTeam(team);
                              }}
                            >
                              Delegate
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              openEdit();
                            }}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchive(team);
                            }}
                          >
                            Archive
                          </button>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedId(expandedId === team.id ? null : team.id);
                            }}
                          >
                            Members
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {expandedId === team.id && (
                    <tr>
                      <td colSpan={5}>
                        <MembersPanel team={team} teams={teams} users={users} onChanged={handleMembersChanged} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {dialogState && (
        <TeamDialog
          team={dialogState.mode === "edit" ? dialogState.team : null}
          teams={teams}
          users={users}
          onClose={() => setDialogState(null)}
          onSaved={() => void refetch()}
        />
      )}

      {delegationTeam && (
        <DelegationDialog
          team={delegationTeam}
          teams={teams}
          users={users}
          onClose={() => setDelegationTeam(null)}
          onChanged={() => void refetch()}
        />
      )}
    </div>
  );
}

export default TeamsAdminPage;
